<template>
  <div class="interface row mb-4">

    <!-- close interface -->
    <div class="col-12 text-right">
      <input-button
        pointable
        hollow
        smaller
        slimmer
        centered
        btnStyle="nok-style"
        inputPlaceholder="x"
        @click="$emit('close', {})" />

    </div>

    <!-- if can migrate -->
    <template v-if="!allEmpty && hasWP && hasSQ">
      <template v-if="!migrating">
        <h5 class="col-12">
          Migracija Sqwiz -- Wordpress
        </h5>
        <p v-if="user.email" class="col-12">
          Uporabnik <b>{{ user.email }}</b>
        </p>

        <div class="col-12">
          <row
            v-for="c of sqwizPages"
            :key="c.id"
            :sqwiz-page="c"
            :wordpress-users="wordpressUsers"
            :user-id="userId"
            @fieldUpdate="updateFields"
            @update="updateRowState" />

        </div>

        <div class="col-12 text-center">
          <input-button
            pointable
            hollow
            smaller
            centered
            btnStyle="ok-style"
            inputPlaceholder="Prenesi"
            :dissabled="!canMigrate"
            @click="migrate" />
        </div>
      </template>
      <div
        v-else
        class="loading text-center col-12 m-3">

        <q-icon
          :allowSelfUpdate="false"
          spin
          width="110px"
          name="spinner" />

        <br />
        <h5 class="mt-2">
          Prenašam objave
        </h5>
      </div>
    </template>

    <!-- no wordpress -->
    <template v-else-if="!hasWP">
      <h5 class="col-12">
        Migracija ni mogoča
      </h5>
      <p class="col-12">
        Uporabnik nima veljavne Wordpress povezave
      </p>
    </template>

    <!-- no sqwiz -->
    <template v-else-if="!hasSQ">
      <h5 class="col-12">
        Migracija ni mogoča
      </h5>
      <p class="col-12">
        Uporabnik nima veljavne Sqwiz povezave
      </p>
    </template>

    <!-- no migration required -->
    <template v-else>
      <h5 class="col-12">
        Migracija ni potrebna
      </h5>
      <p class="col-12">
        Vse objave iz Sqwiz-a so prenešene na Wordpress.
      </p>
    </template>

    <template v-if="error">
      <p class="error mt-3">{{ error }}</p>
    </template>

    <template v-if="warning">
      <p class="warning mt-3">
        {{ warning }}
      </p>
    </template>
  </div>
</template>

<script>
import InputButton from '@/components/standard/input/InputButton'
import QIcon from '@/components/standard/display/QIcon'
import Row from './Row'
import fieldBuilder from '@/assets/modules/apollo/fieldBuilder'
import apiConnector from '@/mixins/apiConnector.js'
import manager from '@/assets/modules/notifications/manager'
import resolver from '@/assets/modules/error/resolver'
import gql from 'graphql-tag'

export default {
  props: {
    userId: {
      type: String,
      requited: true
    }
  },

  data () {
    return {
      user: {},
      migrating: false,
      migration: {},
      empties: {},
      error: null,
      warning: null,
    }
  },

  computed: {
    hasWP () {
      return !!this.wordpressUsers.length
    },

    hasSQ () {
      return !!this.sqwizPages.length
    },

    credentials () {
      return (this.user.credentials || {}).set || []
    },

    sqwizPages () {
      return this.credentials.filter(c => c.type === 'sqwizPage')
    },

    wordpressUsers () {
      return this.credentials.filter(c => c.type === 'wordpressUser').map(p => ({ ...p, val: p.id, label: `${(this.wordpressWebsites[p.meta.websiteId] || {}).label || ''} ${p.label}` }))
    },

    wordpressWebsites () {
      return this.credentials.filter(c => c.type === 'wordpressWebsite').reduce((acc, cur) => {
        acc[cur.id] = cur
        return acc
      }, {})
    },

    canMigrate () {
      // If all sqwiz pages with any mpe's have a wordpress selected
      const filtered = this.sqwizPages.filter(({ id }) => !this.empties[id])
      return filtered.length > 0 && Object.keys(this.migration).length === filtered.length
    },

    allEmpty () {
      return Object.keys(this.empties).length >= this.sqwizPages.length
    }
  },

  methods: {
    updateRowState ({ empty, id }) {
      if (empty) {
        this.$set(this.empties, id, true)
      } else {
        this.$delete(this.empties, id)
      }
    },

    updateFields (e) {
      if (!e.value) {
        this.$delete(this.migration, e.input)
      } else {
        this.$set(this.migration, e.input, e.value)
      }
    },

    prepPayload () {
      let map = {}
      // Reverse map so wordpress points to sqwiz
      for (const [ sqwiz, wordpress ] of Object.entries(this.migration)) {
        if (!map[wordpress]) {
          map[wordpress] = []
        }
        map[wordpress].push(sqwiz)
      }

      // Prepare mutation structure
      const credentials = []
      for (const [ wp, sqwiz = [] ] of Object.entries(map)) {
        credentials.push({ wp, sqwiz })
      }

      return credentials
    },

    migrate () {
      this.error = null
      this.warning = null

      const credentials = this.prepPayload()

      // Send mutation
      this.migrating = true

      const okCallback = (data) => {
        this.migrating = false

        const { createFails = [], deleteFails = [] } = data
        if (createFails.length) {
          this.warning = `Nekaj ustvarjanj je spodletelo: ${JSON.stringify(createFails)}`
        } else if (deleteFails.length) {
          this.warning = `Nekaj brisanj je spodletelo: ${JSON.stringify(deleteFails)}`
        }

        this.$emit('refetch')
      }

      const failCallback = (_, e) => {
        this.migrating = false
        this.error = e.message
        this.$emit('refetch')
      }

      let {mutationString, dataObject, name} = fieldBuilder
        .buildGqlObject({ credentials, userId: this.userId }, 'migrateSqwiz', 'mutation', [], {
          credentials: '[SqwizWebsiteWordpressUserMigrationInput!]',
          userId: 'UUID'
        })

    this.sendMutation(
      mutationString,
      dataObject,
      okCallback,
      failCallback,
      name)
    }
  },

  components: {
    InputButton,
    'q-icon': QIcon,
    Row,
  },

  mixins: [apiConnector],

  // Apollo...
  apollo: {
    user: {
      query: gql`query ($id: UUID!) {
        user: user (id: $id) {
          id,
          email,
          credentials {
            set {
              type,
              label,
              id,
              identity,
              visible,
              meta {
                ... on UserCredentialsSqwizPage {
                  viewUrl
                },

                ... on UserCredentialsWordpressWebsite {
                  url
                },
                ... on UserCredentialsWordpressUser {
                  name,
                  websiteId
                }
              }
            }
          }
        }
      }`,
      variables () {
        return {
          id: this.userId
        }
      },
      fetchPolicy: 'cache-and-network'
    }
  },
}
</script>

<style scoped>
.interface {
  padding: 20px 10px;
  border: 1px solid #FE2B41;
  border-radius: 4px;
  background-color: #ffffff;
}

.error {
  color: #FE2B41;
  font-weight: bold;
}

.warning {
  color: #F1A5AC;
  font-weight: bold;
}

</style>



// WEBPACK FOOTER //
// src/components/standard/display/migration/sqwiz/Interface.vue?730ed1a0